import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, Mail, MapPin } from 'lucide-react';

import tableIconUserShare from '../../../../assets/images/tabler-icon-user-share.svg';
import Notification from '../../../../components/Notification/Notification';
import ButtonWithIcon from '../../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import ReferAccessButtonTooltip from '../../../../components/shared/ReferAccessButtonTooltip/ReferAccessButtonTooltip';
import { useReferralAccess } from '../../../../hooks/useReferralAccess';
import urls from '../../../../routing/urls';
import { selectCurrentOrganization } from '../../../../selectors/organizationSelectors';
import { getMatchingJobs } from '../../../../services/browseService';
import * as networkService from '../../../../services/networkService';
import JobSection from '../../../Browse/OpeningsListElement/JobSection';

import styles from '../../../Browse/OpeningsListElement/OpeningsListElement.module.scss';

function transformJobForJobSection(job) {
  return {
    id: job.id,
    uuid: job.uuid,
    title: job.title,
    organization_name: job.company_name,
    job_location: job.location,
    work_type: job.work_type,
    work_place_type: job.work_place_type,
    category: job.category,
    external_job_link: job.external_job_link,
    published_date: job.created_at ? new Date(job.created_at * 1000).toISOString() : null,
    skills: job.skills,
    job_experience_name: job.job_experience,
  };
}

const ContactRecommendations = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { contactId } = useParams();
  const organization = useSelector(selectCurrentOrganization);
  const { blocked, message, tooltipMessage } = useReferralAccess(organization);

  const [contact, setContact] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contactId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchData = async () => {
      try {
        const contactRes = await networkService.getContactDetails(contactId);
        const contactData = contactRes.data?.payload || contactRes.data;
        if (!cancelled) setContact(contactData);

        const jobsRes = await getMatchingJobs(contactId, { limit: 10, include_reasons: true });
        const jobsPayload = jobsRes.data?.payload || jobsRes.data;
        if (!cancelled) setJobs(jobsPayload.jobs || []);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const contactEmail = contact?.user_contact_emails_attributes?.[0]?.email || contact?.email;

  const handleReferClick = (jobUuid) => {
    if (blocked) {
      Notification.error(message || t('referralAccess.referDisabled'));
      return;
    }

    navigate(`${urls.refer}?refer_for=${jobUuid}&c_id=${contactId}`);
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Skeleton width={120} height={20} className="mb-4" />
        <Skeleton height={100} className="mb-6 rounded-lg" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={`skel-${i}`} height={140} className="rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(urls.network)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 bg-transparent border-0 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('contactRecommendations.backToNetwork')}
        </button>
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-gray-500">{t('contactRecommendations.error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <button
        type="button"
        onClick={() => navigate(urls.network)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 bg-transparent border-0 cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('contactRecommendations.backToNetwork')}
      </button>

      {contact && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-base font-semibold flex-shrink-0">
              {contact.full_name
                ? contact.full_name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase()
                : 'U'}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 mb-0">{contact.full_name}</h2>
              {(contact.job_title || contact.company) && (
                <p className="text-sm text-gray-600 mb-0">
                  {contact.job_title}
                  {contact.company && ` @ ${contact.company}`}
                </p>
              )}
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {contact.location && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {contact.location}
                  </span>
                )}
                {contactEmail && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {contactEmail}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Briefcase className="w-4 h-4" />
        {t('contactRecommendations.jobsTitle', { count: jobs.length })}
      </h3>

      {jobs.length === 0 ? (
        <div className="flex items-center justify-center py-12 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">{t('contactRecommendations.noJobs')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((item) => {
            const jobData = transformJobForJobSection(item.job);
            return (
              <div key={item.job?.uuid || item.job?.id} className="rounded-lg border border-gray-200">
                <div className="flex items-center justify-between px-3 pt-3 pb-0">
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                    {t('matchScore', { score: Math.floor(item.matching_score * 100) })}
                  </span>
                  <ReferAccessButtonTooltip blocked={blocked} tooltipText={tooltipMessage}>
                    <ButtonWithIcon
                      text={t('buttons.refer')}
                      img={tableIconUserShare}
                      imgClassName="btn-icon-36"
                      textClassName="btn-icon-text-tertiary-40"
                      variant="tertiary-36"
                      onClick={() => handleReferClick(item.job?.uuid)}
                      disabled={blocked}
                    />
                  </ReferAccessButtonTooltip>
                </div>

                <div className={styles.recommendationItem} role="presentation">
                  <JobSection job={jobData} shouldShowButtons={false} />
                </div>

                {item.matching_reason && (
                  <div className="mx-3 mb-3 rounded-md bg-purple-50 border border-purple-100 px-3 py-2">
                    <p className="text-xs font-semibold text-purple-700 mb-0.5">{t('matchingReason.title')}</p>
                    <p className="text-xs text-purple-600 leading-relaxed mb-0">{item.matching_reason}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactRecommendations;
